import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchWithAuth } from '../../../utils/api';
import { tierOf, TIER_LABEL } from '../../../utils/roleTier';
import { PencilIcon, TrashIcon, PlusIcon, UserGroupIcon, UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAccess, Capability } from '../../../hooks/useAccess';
import { getActingOrg } from '../../../utils/actingOrg';
import V3UserForm from './V3UserForm';
import V3EmailLinkDialog from './V3EmailLinkDialog';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';
import { APIErrorException } from '../../../services/types/errors';

// User interface from the API
interface User {
  user_id: string;
  username: string;
  name?: string | null;
  email: string;
  organization_id: string;
  role: string;
  rbac_role?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Form data interface for creating/updating users. No organization field — the
// target org is fixed by the caller's context server-side.
interface UserFormData {
  user_id?: string;
  name: string;
  username?: string;
  email: string;
  password?: string;
  role: string;
  is_active?: boolean;
}

// What the add-user response says about the invitation email.
interface UserCreatedResponse {
  email_sent?: boolean;
  email_to?: string | null;
}

const V3UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [emailLinkUser, setEmailLinkUser] = useState<User | null>(null);
  const { user: currentUser } = useAuth();
  const location = useLocation();
  
  // If we were passed an organization ID from the organization management page,
  // we'll filter users by that organization
  const filterOrgId = location.state?.orgId;
  const { can } = useAccess();
  // Creating a user forces its org to the caller's context, so it only lands
  // correctly when that context IS the org on screen: an org admin in their own
  // org, or a platform admin ACTING as an org. In global platform scope (no
  // acting) create would drop the user into the platform home org, and in the
  // filtered "View Users" peek into the admin's home org — both wrong, so hide
  // create there and point the admin at acting mode.
  const isGlobalPlatformScope = can(Capability.PlatformManage) && !getActingOrg();
  const canCreateHere = !filterOrgId && !isGlobalPlatformScope;
  // Nothing returns an organisation admin's own org name, so the copy names the
  // acting org when there is one and says "your organization" otherwise. The
  // example email domain is the admin's own, unless they are acting as an org.
  const actingOrg = getActingOrg();
  const firmName = actingOrg?.name ?? 'your organization';
  const emailDomain = actingOrg ? null : currentUser?.email?.split('@')[1]?.toLowerCase() || null;

  // Refetch when the org filter changes — this component is reused when
  // navigating from a filtered peek (state.orgId set) to the ordinary Users
  // link (no state), so without filterOrgId as a dep the previous org's users
  // would stay on screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUsers();
  }, [filterOrgId]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      let endpoint = '/v1/auth/admin/users';
      
      // If filtering by organization, add the organization_id parameter
      if (filterOrgId) {
        endpoint += `?organization_id=${filterOrgId}`;
      }
      
      const data = await fetchWithAuth(endpoint);
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = () => {
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };
  
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };
  
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetchWithAuth(`/v1/auth/admin/users/${userId}`, {
        method: 'DELETE'
      });
      
      // Remove the user from the state without needing a server refresh
      setUsers(currentUsers => 
        currentUsers.filter(user => user.user_id !== userId)
      );
      
      toast.success('User deleted successfully');
    } catch (err) {
      toast.error(err instanceof APIErrorException ? err.error.message : 'Failed to delete user. Please try again.');
      console.error(err);
    }
  };
  
  // Errors propagate to the form, which shows them in place.
  const handleSubmitUser = async (userData: UserFormData) => {
    if (userData.user_id) {
      await fetchWithAuth(`/v1/auth/admin/users/${userData.user_id}`, {
        method: 'PUT',
        body: JSON.stringify(userData)
      });
      setIsModalOpen(false);
      fetchUsers();
      toast.success('User updated successfully');
      return;
    }

    const created: UserCreatedResponse | null = await fetchWithAuth('/v1/auth/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    setIsModalOpen(false);
    fetchUsers();
    // A response without the email fields reads as not sent.
    const email = created?.email_to || userData.email;
    if (created?.email_sent) {
      toast.success(`${userData.name} was added. We've emailed ${email} a link to set their password.`);
    } else if (userData.password) {
      toast.success(`${userData.name} was added.`);
    } else {
      toast.warning(`${userData.name} was added, but the email to ${email} didn't send. Use the email icon on their row to try again, or copy the link from there.`);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
          <p className="text-red-800 mb-4">{error}</p>
          <button 
            onClick={fetchUsers}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Heading level={2} className="text-gray-900 flex items-center">
              <UserGroupIcon className="w-7 h-7 mr-3 text-gray-700" />
              {filterOrgId ? 'Organization Users' : 'Users & Permissions'}
            </Heading>
            {canCreateHere && (
              <p className="text-gray-600 mt-1">
                Add people to {firmName} and choose what they can do.
              </p>
            )}
          </div>
          {canCreateHere && (
            <button
              onClick={handleCreateUser}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center hover:bg-blue-700 transition-colors shadow-sm"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add user
            </button>
          )}
        </div>
        {!canCreateHere && (
          <p className="-mt-4 mb-6 text-sm text-gray-500">
            {filterOrgId
              ? "Viewing another organization's users. To add a user here, switch to acting as this organization first."
              : 'Select and act as an organization to add users to it.'}
          </p>
        )}
        
        {/* Users Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Added
                  </th>
                  <th className="px-6 py-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name || user.username}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{TIER_LABEL[tierOf(user.rbac_role, user.role)]}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{user.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <Timestamp value={user.created_at} format="absolute" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button 
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                            onClick={() => setEmailLinkUser(user)}
                            title="Email a sign-in link"
                          >
                            <EnvelopeIcon className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                            onClick={() => handleEditUser(user)}
                            title="Edit user"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button 
                            className={`p-2 rounded-lg transition-colors ${
                              user.username === currentUser?.username
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'hover:bg-red-50 text-red-600 hover:text-red-700'
                            }`}
                            disabled={user.username === currentUser?.username}
                            onClick={() => user.username !== currentUser?.username && handleDeleteUser(user.user_id)}
                            title={user.username === currentUser?.username ? "You cannot delete your own account" : "Delete user"}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer Note */}
        <div className="mt-4 text-sm text-gray-500">
          <p>* You cannot delete your own account</p>
        </div>
      </div>
      
      {/* User Form Modal */}
      {isModalOpen && (
        <V3UserForm
          user={selectedUser}
          onSubmit={handleSubmitUser}
          onClose={() => setIsModalOpen(false)}
          firmName={firmName}
          emailDomain={emailDomain}
        />
      )}
      
      {emailLinkUser && (
        <V3EmailLinkDialog user={emailLinkUser} onClose={() => setEmailLinkUser(null)} />
      )}
    </div>
  );
};

export default V3UsersManagement;